import { BASE_API_URL, BASE_WS_URL } from '@/const/app';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';


let client = null;
let clientSession = null;
let clientRequest = null;

let cachedToken = null;
let tokenRequest = null;

const TOKEN_REFRESH_MARGIN = 60 * 1000;

const requestConnectionToken = (sessionToken) => {
  const headers = { Accept: 'application/json' };
  if (sessionToken) headers.Authorization = `Bearer ${sessionToken}`;

  return fetch(`${BASE_API_URL}/ws/token`, { headers, cache: 'no-store' })
    .then(async (response) => {
      const result = await response.json();

      if (!response.ok || typeof result?.data?.token !== 'string') {
        throw new Error(result?.messages?.[0]?.message || 'Failed to load the realtime token.');
      }

      cachedToken = {
        session: sessionToken,
        token: result.data.token,
        accountId: result.data.account_id ?? null,
        expiresAt: Date.now() + (result.data.expires_in * 1000) - TOKEN_REFRESH_MARGIN,
      };

      return cachedToken;
    })
    .finally(() => {
      tokenRequest = null;
    });
};

const getConnectionToken = (sessionToken) => {
  if (cachedToken?.session === sessionToken && cachedToken.expiresAt > Date.now()) {
    return Promise.resolve(cachedToken);
  }

  if (!tokenRequest) {
    tokenRequest = requestConnectionToken(sessionToken);
  }

  return tokenRequest;
};

const connect = (sessionToken) => {
  if (clientRequest && clientSession === sessionToken) return clientRequest;

  clientSession = sessionToken;
  clientRequest = import('centrifuge').then(({ Centrifuge }) => {
    if (clientSession !== sessionToken) return clientRequest;

    client?.disconnect();
    cachedToken = null;
    client = new Centrifuge(BASE_WS_URL, {
      getToken: () => getConnectionToken(sessionToken).then((data) => data.token),
    });

    client.connect();
    return client;
  });

  clientRequest.catch(() => {
    if (clientSession === sessionToken) clientRequest = null;
  });

  return clientRequest;
};

export function useCentrifugo() {
  const session = useSelector((state) => state.auth.session);
  const sessionToken = session?.token ?? null;

  const [state, setState] = useState({ client: null, accountId: null });

  useEffect(() => {
    let active = true;

    connect(sessionToken)
      .then((instance) => getConnectionToken(sessionToken).then(
        (data) => ({ client: instance, accountId: data.accountId }),
        () => ({ client: instance, accountId: null }),
      ))
      .then((next) => {
        if (active) setState(next);
      })
      .catch(() => { });

    const handlePageHide = (event) => {
      if (event.persisted) client?.disconnect();
    };

    const handlePageShow = (event) => {
      if (event.persisted) client?.connect();
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      active = false;
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [sessionToken]);

  return state;
}

const attachSubscription = (centrifugo, channel, { onData, onError, getToken }) => {
  const subscription = centrifugo.getSubscription(channel)
    ?? centrifugo.newSubscription(channel, getToken ? { getToken } : undefined);

  const handlePublication = (ctx) => onData(ctx.data, { fromClient: Boolean(ctx.info) });
  const handleError = (ctx) => onError(ctx);

  subscription.on('publication', handlePublication);
  subscription.on('error', handleError);

  subscription.subscribe();

  return () => {
    subscription.off('publication', handlePublication);
    subscription.off('error', handleError);
    subscription.unsubscribe();
    centrifugo.removeSubscription(subscription);
  };
};

export function useChannel(channel, onData, options = {}) {
  const { onError, getToken } = options;

  const { client: centrifugo } = useCentrifugo();

  const dataRef = useRef(onData);
  const errorRef = useRef(onError);
  const tokenRef = useRef(getToken);

  useEffect(() => {
    dataRef.current = onData;
    errorRef.current = onError;
    tokenRef.current = getToken;
  }, [onData, onError, getToken]);

  useEffect(() => {
    if (!centrifugo || !channel) return undefined;

    return attachSubscription(centrifugo, channel, {
      onData: (data, meta) => dataRef.current?.(data, meta),
      onError: (error) => errorRef.current?.(error),
      getToken: getToken ? () => tokenRef.current() : undefined,
    });
  }, [centrifugo, channel, !!getToken]);
}

export function publishToChannel(channel, data) {
  const subscription = channel ? client?.getSubscription(channel) : null;

  if (!subscription) return Promise.resolve(false);

  return subscription.publish(data).then(() => true).catch(() => false);
}

export function subscribeToChannel(channel, onData, options = {}) {
  const { onError } = options;

  if (!client || !channel) {
    onError?.(new Error('Realtime connection is not available.'));
    return () => { };
  }

  return attachSubscription(client, channel, {
    onData: (data) => onData?.(data),
    onError: (error) => onError?.(error),
  });
}

export default useCentrifugo;
